import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Link,
  useColorModeValue,
  useToast,
  Container,
  Icon
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import api from "../../../services/api";
import menomV2Logo from "../../../assets/img/menomV2Logo.png";

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const toast = useToast();
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });

  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.300");

  useEffect(() => {
    if (!token) {
      toast({
        title: "Geçersiz link",
        description: "Şifre sıfırlama linki geçersiz.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate('/auth/sign-in');
    }
  }, [token, navigate, toast]);

  const handleClick = () => setShow(!show);
  const handleClickConfirm = () => setShowConfirm(!showConfirm);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Hata",
        description: "Şifreler eşleşmiyor.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (formData.password.length < 8) {
      toast({
        title: "Hata",
        description: "Şifre en az 8 karakter olmalıdır.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`/api/business/auth/reset-password/${token}`, {
        password: formData.password
      });

      if (response.data?.success) {
        toast({
          title: "Başarılı",
          description: response.data.message || "Şifreniz başarıyla sıfırlandı.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        // 2 saniye sonra login sayfasına yönlendir
        setTimeout(() => {
          navigate('/auth/sign-in');
        }, 2000);
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Şifre sıfırlanırken bir hata oluştu. Link süresi dolmuş olabilir.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" py={8}>
      <Container maxW="md">
        <Flex
          direction="column"
          w="100%"
          bg="white"
          borderRadius="xl"
          boxShadow="xl"
          p={8}
        >
          <Flex direction="column" align="center" mb={8}>
            <Box mb={4}>
              <img src={menomV2Logo} alt="Menom" style={{ height: '80px', width: 'auto' }} />
            </Box>
            <Heading size="xl" color={textColor} mb={2}>
              Yeni Şifre Belirle
            </Heading>
            <Text color={textColorSecondary} fontSize="sm" textAlign="center">
              Yeni şifrenizi belirleyin
            </Text>
          </Flex>

          <form onSubmit={handleSubmit}>
            <FormControl mb={6}>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Yeni Şifre
              </FormLabel>
              <InputGroup size="md">
                <Input
                  variant="auth"
                  fontSize="sm"
                  type={show ? "text" : "password"}
                  placeholder="Min. 8 karakter"
                  mb="24px"
                  fontWeight="500"
                  size="lg"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={useColorModeValue("gray.400", "white")}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>

              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Şifre Tekrar
              </FormLabel>
              <InputGroup size="md">
                <Input
                  variant="auth"
                  fontSize="sm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Şifrenizi tekrar girin"
                  mb="24px"
                  fontWeight="500"
                  size="lg"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={useColorModeValue("gray.400", "white")}
                    _hover={{ cursor: "pointer" }}
                    as={showConfirm ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClickConfirm}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              type="submit"
              isLoading={loading}
            >
              Şifreyi Sıfırla
            </Button>

            <Flex justifyContent="center" alignItems="center">
              <Text color={textColorSecondary} fontWeight="400" fontSize="14px">
                <Link
                  as={RouterLink}
                  to="/auth/sign-in"
                  color="brand.500"
                  fontWeight="500"
                  textDecoration="none"
                  _hover={{ textDecoration: "none" }}
                >
                  Giriş sayfasına dön
                </Link>
              </Text>
            </Flex>
          </form>
        </Flex>
      </Container>
    </Box>
  );
}

export default ResetPassword;
